import { useEffect, useState } from 'react'
import { getWalletDetails } from '../api/api'
import { Column, useSortBy, useTable } from 'react-table'
import { columns } from '../utils/helpers/helper'
import { useInfiniteQuery, useQuery } from 'react-query'
import { TConnectionTime, WalletDetailsResponse } from '../api/model'
import moment from 'moment'
import useCustomStore from '../hooks/useCustomStore'
import { TimeRange } from '../api/model'
import { ReactComponent as SortArrow } from '../assets/icons/chevron-down.svg'
import UserColumn from './UserColumn'
import { InView } from 'react-intersection-observer'
import Loader from './Loading'

const StatsTable = () => {
  const [tableData, setTableData] = useState<
    Record<string, string | number | JSX.Element | null>[]
  >([])
  const { title, selectedOption, search, setSortBy, sortBy, sortDirection, setSortDirection } =
    useCustomStore()

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery<WalletDetailsResponse>(
    ['walletDetails', title.value, selectedOption, search, sortBy, sortDirection],
    async ({ pageParam = 1 }) => {
      return await getWalletDetails({
        date: title.value as TimeRange,
        connectionTime: selectedOption as TConnectionTime,
        ...(search && { search }),
        ...(sortBy && { sortBy }),
        ...(sortDirection && { sortDirection }),
        page: pageParam,
      })
    },
    {
      getNextPageParam: (lastPage, pages) => {
        return lastPage.count > pages.length * 10 ? pages.length + 1 : undefined
      },
    },
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: columns as Column<Record<string, string | number | JSX.Element | null>>[],
      data: tableData,
    },
    useSortBy,
  )

  useEffect(() => {
    if (data) {
      const filteredData = data.pages
        .flatMap((item) => item.rows)
        .map((item, idx) => {
          return {
            id: idx + 1,
            createdAt: moment(item.createdAt).format('MM.DD.YYYY'),
            updatedAt: moment(item.updatedAt).format('MM.DD.YYYY'),
            totalNettyworth: item.totalNettyworth || 'N/A',
            ethUsdValue: item.ethUsdValue ? Number(item.ethUsdValue) : 'N/A',
            nftUsdBalance: item.nftUsdBalance ? Number(item.nftUsdBalance) : 'N/A',
            username: (
              <UserColumn
                image={item.user?.profile_picture}
                username={item.user?.name}
                address={item.address}
              />
            ),
            walletAddress: item.address,
            email: item.user?.email ? item.user.email : 'N/A',
          }
        })
      setTableData(filteredData)
    }
  }, [data])

  const isAsc = sortDirection === 'asc'

  const handleSort = (column: Column<Record<string, string | number | JSX.Element | null>>) => {
    setSortDirection((prev) => {
      // @ts-ignore
      return sortBy === (column.sortId || column.id) ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'
    })
    // @ts-ignore
    setSortBy(column.sortId || (column?.id ? column?.id?.toString() : ''))
  }

  return (
    <div className='mt-[106px]'>
      <table
        {...getTableProps()}
        className='w-full block overflow-x-auto whitespace-nowrap xl:table'
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  // @ts-ignore
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className='text-sm font-medium text-left pl-12 pb-3'
                >
                  <div onClick={() => handleSort(column)} className='flex items-center gap-3'>
                    {column.render('Header')}
                    {/* @ts-ignore */}
                    {isAsc && (column.sortId || column.id) === sortBy ? (
                      <SortArrow className='rotate-180' />
                    ) : (
                      <SortArrow />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className='font-space-mono' {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            return (
              <tr
                {...row.getRowProps()}
                className='text-[14px] font-medium border-b border-coal-border'
                onClick={() =>
                  window.open(`https://etherscan.io/address/${row.original.walletAddress}`)
                }
              >
                {row.cells.map((cell) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className='first:font-bold first:text-[22px] py-7 pl-12'
                    >
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {isLoading ? (
        <Loader />
      ) : (
        hasNextPage && (
          <InView
            className='h-[100px]'
            onChange={(inView) => {
              if (inView && hasNextPage) {
                fetchNextPage()
              }
            }}
          />
        )
      )}
    </div>
  )
}

export default StatsTable
